import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { adminService } from '../../services/admin.service';
import AdminNavbar from '../../components/admin/AdminNavbar';
import { FaCopy, FaCheck } from 'react-icons/fa';

const ApiKeyForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }

    try {
      setLoading(true);
      const response = await adminService.createApiKey({ name: name.trim() });
      setCreatedKey(response);
      toast.success('API key creada');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al crear la API key');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!createdKey?.key) return;
    try {
      await navigator.clipboard.writeText(createdKey.key);
      setCopied(true);
      toast.success('API key copiada');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  return (
    <>
      <AdminNavbar />
      <Container>
        <Header>
          <Title>Nueva API Key</Title>
          <BackButton type="button" onClick={() => navigate('/pmpl/admin/api-keys')}>
            Volver
          </BackButton>
        </Header>

        {!createdKey ? (
          <Form onSubmit={handleSubmit}>
            <Section>
              <SectionTitle>Datos</SectionTitle>
              <FormGroup>
                <Label>Nombre</Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ej. Integración Partner X"
                  required
                />
                <HelpText>
                  Identifica para qué se usa esta clave. La clave secreta se genera automáticamente.
                </HelpText>
              </FormGroup>
            </Section>

            <Actions>
              <SubmitButton type="submit" disabled={loading}>
                {loading ? 'Creando...' : 'Crear API Key'}
              </SubmitButton>
            </Actions>
          </Form>
        ) : (
          <ResultCard>
            <Warning>
              Copia la clave ahora. No se volverá a mostrar.
            </Warning>
            <ResultLabel>Nombre</ResultLabel>
            <ResultValue>{createdKey.apiKey?.name}</ResultValue>
            <ResultLabel>API Key</ResultLabel>
            <KeyBox>
              <KeyText>{createdKey.key}</KeyText>
              <CopyButton type="button" onClick={handleCopy}>
                {copied ? <FaCheck /> : <FaCopy />}
                {copied ? 'Copiada' : 'Copiar'}
              </CopyButton>
            </KeyBox>
            <DoneButton type="button" onClick={() => navigate('/pmpl/admin/api-keys')}>
              Hecho
            </DoneButton>
          </ResultCard>
        )}
      </Container>
    </>
  );
};

const Container = styled.div`
  padding: 2rem;
  max-width: 700px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  color: #ffd700;
  margin: 0;
`;

const BackButton = styled.button`
  background: transparent;
  color: #fff;
  border: 1px solid #444;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Section = styled.div`
  background: #2a2a2a;
  border-radius: 8px;
  padding: 1.5rem;
`;

const SectionTitle = styled.h2`
  color: #ffd700;
  font-size: 1.1rem;
  margin: 0 0 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: #ccc;
  font-size: 0.9rem;
`;

const Input = styled.input`
  background: #1a1a1a;
  border: 1px solid #444;
  border-radius: 4px;
  padding: 0.75rem;
  color: #fff;
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: #ffd700;
  }
`;

const HelpText = styled.p`
  color: #888;
  font-size: 0.85rem;
  margin: 0;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const SubmitButton = styled.button`
  background: #ffd700;
  color: #1a1a1a;
  border: none;
  border-radius: 4px;
  padding: 0.75rem 1.5rem;
  font-weight: bold;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #ffed4a;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ResultCard = styled.div`
  background: #2a2a2a;
  border-radius: 8px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Warning = styled.p`
  margin: 0 0 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 6px;
  color: #fca5a5;
  font-weight: 500;
`;

const ResultLabel = styled.span`
  color: #999;
  font-size: 0.8rem;
`;

const ResultValue = styled.span`
  color: #fff;
  font-size: 1rem;
  margin-bottom: 0.5rem;
`;

const KeyBox = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: stretch;
  flex-wrap: wrap;
`;

const KeyText = styled.code`
  flex: 1;
  min-width: 200px;
  background: #1a1a1a;
  border: 1px solid #444;
  border-radius: 4px;
  padding: 0.75rem;
  color: #ffd700;
  font-size: 0.85rem;
  word-break: break-all;
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 215, 0, 0.1);
  color: #ffd700;
  border: 1px solid rgba(255, 215, 0, 0.3);
  border-radius: 4px;
  padding: 0.75rem 1rem;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 215, 0, 0.2);
  }
`;

const DoneButton = styled.button`
  margin-top: 1rem;
  align-self: flex-end;
  background: #ffd700;
  color: #1a1a1a;
  border: none;
  border-radius: 4px;
  padding: 0.75rem 1.5rem;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    background: #ffed4a;
  }
`;

export default ApiKeyForm;
